import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { AdminAssurancePolicyPackPanel } from "./AdminAssurancePolicyPackPanel";

type SourceVersion={
  versionId:string;
  versionNo:number;
  previousVersionId?:string|null;
  changeType:string;
  contentHash:string;
  sourceSnapshot:Record<string,unknown>;
  effectiveFrom?:string|null;
  effectiveUntil?:string|null;
  createdAt:string;
};

type Rule={
  ruleId:string;
  ruleKey:string;
  versionNo:number;
  previousRuleId?:string|null;
  sourceVersionId:string;
  title:string;
  ruleText:string;
  obligationType:string;
  severity:string;
  applicabilitySpec:Record<string,unknown>;
  actionSpec:Record<string,unknown>;
  evidenceSpec:Record<string,unknown>;
  status:string;
  verifiedAt?:string|null;
};

type Source={
  sourceId:string;
  sourceKey:string;
  sourceType:string;
  jurisdictionCode:string;
  authorityName:string;
  title:string;
  canonicalUri?:string|null;
  status:string;
  currentVersion:number;
  recordVersion:number;
  verifiedAt?:string|null;
  versions:SourceVersion[];
  rules:Rule[];
};

type School={
  schoolId:string;
  schoolName:string;
  academicYears:Array<{
    academicYearId:string;
    yearCode:string;
    status:string;
  }>;
};

type Decision={
  decisionId:string;
  decisionType:string;
  status:string;
  reason?:string|null;
  version:number;
  confirmedAt?:string|null;
};

type Applicability={
  applicabilityId:string;
  ruleId:string;
  ruleTitle:string;
  ruleSeverity:string;
  schoolId:string;
  schoolName:string;
  academicYearId:string;
  yearCode:string;
  gradeScope:string[];
  engineOutcome:string;
  rationale:Record<string,unknown>;
  requiresHumanGate:boolean;
  computedAt:string;
  decisions:Decision[];
};

type WorkItem={
  workItemId:string;
  schoolDecisionId:string;
  ruleId:string;
  ruleTitle:string;
  ruleSeverity:string;
  schoolId:string;
  schoolName:string;
  academicYearId:string;
  yearCode:string;
  assigneePersonId:string;
  assigneeName:string;
  title:string;
  rationale:string;
  dueAt:string;
  evidenceRequirement:Record<string,unknown>;
  status:string;
  version:number;
  completionNote?:string|null;
  createdAt:string;
  updatedAt:string;
};

type ReviewDecision={
  decisionId:string;
  decision:string;
  reviewerPersonId:string;
  reviewerName:string;
  checklist:Record<string,unknown>;
  notes?:string|null;
  taskAction:string;
  auditReason:string;
  decidedAt:string;
};

type ChangeReview={
  reviewId:string;
  sourceId:string;
  sourceTitle:string;
  fromVersionId:string;
  toVersionId:string;
  severity:string;
  changeType:string;
  status:string;
  guidanceFrozen:boolean;
  requiredApprovals:number;
  impactScope:Record<string,unknown>;
  version:number;
  createdAt:string;
  updatedAt:string;
  decisions:ReviewDecision[];
};

type Catalog={
  sources:Source[];
  schools:School[];
  reviewers:Array<{
    personId:string;
    displayName:string;
    email?:string|null;
  }>;
  applicability:Applicability[];
  workItems:WorkItem[];
  changeReviews:ChangeReview[];
};

type SchoolMember={
  personId:string;
  displayName:string;
  email?:string|null;
  roles:string[];
};

type Tab="SOURCE"|"APPLICABILITY"|"WORK"|"CHANGE";

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function sourceStatus(value:string){
  if(value==="DRAFT") return "검증 대기";
  if(value==="VERIFIED") return "검증 완료";
  if(value==="SUPERSEDED") return "대체됨";
  if(value==="RETIRED") return "종료";
  return value;
}

function workStatus(value:string){
  const map:Record<string,string>={
    PROPOSED:"제안",
    APPROVED:"승인",
    IN_PROGRESS:"진행 중",
    COMPLETED:"완료",
    BLOCKED:"차단",
    CANCELLED:"취소"
  };
  return map[value] || value;
}

function reviewStatus(value:string){
  const map:Record<string,string>={
    HUMAN_REVIEW_REQUIRED:"사람 검토 필요",
    EVIDENCE_PENDING:"추가근거 대기",
    ESCALATED:"상위검토",
    DISMISSED:"영향 없음",
    IMPACT_CONFIRMED:"영향 확인",
    SCHOOL_APPLICABILITY_RECHECK:"학교 적용 재검토",
    VERIFIED_NO_TEACHER_ACTION:"교사 조치 없음",
    TEACHER_QUEUE_READY:"교사 업무 반영 준비",
    TASK_UPDATE_APPROVED:"업무 변경 승인",
    CLOSED:"종료"
  };
  return map[value] || value;
}

function parseObject(value:string){
  const parsed=JSON.parse(value);
  if(!parsed || Array.isArray(parsed) || typeof parsed!=="object"){
    throw new Error("JSON 객체 형식이어야 합니다.");
  }
  return parsed as Record<string,unknown>;
}

export function AdminAssuranceGovernance({client}:{client:PlatformClient}){
  const [catalog,setCatalog]=useState<Catalog|null>(null);
  const [tab,setTab]=useState<Tab>("SOURCE");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  const [selectedSourceId,setSelectedSourceId]=useState("");
  const [sourceKey,setSourceKey]=useState("");
  const [sourceType,setSourceType]=useState("GUIDELINE");
  const [jurisdiction,setJurisdiction]=useState("KR");
  const [authority,setAuthority]=useState("");
  const [sourceTitle,setSourceTitle]=useState("");
  const [canonicalUri,setCanonicalUri]=useState("");
  const [sourceSnapshot,setSourceSnapshot]=useState('{"title":"","text":""}');

  const [ruleKey,setRuleKey]=useState("");
  const [ruleTitle,setRuleTitle]=useState("");
  const [ruleText,setRuleText]=useState("");
  const [obligation,setObligation]=useState("REQUIRED");
  const [ruleSeverity,setRuleSeverity]=useState("P2");

  const [changeSnapshot,setChangeSnapshot]=useState('{"title":"","text":""}');
  const [changeType,setChangeType]=useState("SEMANTIC");
  const [changeSeverity,setChangeSeverity]=useState("P2");
  const [changeApprovals,setChangeApprovals]=useState("1");

  const [selectedRuleId,setSelectedRuleId]=useState("");
  const [selectedSchoolId,setSelectedSchoolId]=useState("");
  const [selectedYearId,setSelectedYearId]=useState("");
  const [gradeScope,setGradeScope]=useState("");
  const [candidateNote,setCandidateNote]=useState("");

  const [selectedApplicabilityId,setSelectedApplicabilityId]=useState("");
  const [decisionType,setDecisionType]=useState("APPLICABLE");
  const [decisionReason,setDecisionReason]=useState("");

  const [selectedDecisionId,setSelectedDecisionId]=useState("");
  const [schoolMembers,setSchoolMembers]=useState<SchoolMember[]>([]);
  const [assigneeId,setAssigneeId]=useState("");
  const [workTitle,setWorkTitle]=useState("");
  const [workRationale,setWorkRationale]=useState("");
  const [dueAt,setDueAt]=useState("");
  const [completionNote,setCompletionNote]=useState("");

  const [selectedReviewId,setSelectedReviewId]=useState("");
  const [reviewDecision,setReviewDecision]=useState("APPROVE_CHANGE");
  const [taskAction,setTaskAction]=useState("MARK_REVIEW_REQUIRED");
  const [auditReason,setAuditReason]=useState("");
  const [reviewNotes,setReviewNotes]=useState("");
  const [checks,setChecks]=useState({
    source:true,meaning:true,scope:true,impact:true
  });

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_assurance_catalog").then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Catalog;
      setCatalog(next);
      setSelectedSourceId((current)=>
        next.sources.some((item)=>item.sourceId===current)
          ? current
          : next.sources[0]?.sourceId || ""
      );

      const verifiedRules=next.sources.flatMap((source)=>
        source.rules.filter((rule)=>rule.status==="VERIFIED")
      );
      setSelectedRuleId((current)=>
        verifiedRules.some((rule)=>rule.ruleId===current)
          ? current
          : verifiedRules[0]?.ruleId || ""
      );

      setSelectedSchoolId((current)=>
        next.schools.some((school)=>school.schoolId===current)
          ? current
          : next.schools[0]?.schoolId || ""
      );

      setSelectedApplicabilityId((current)=>
        next.applicability.some((item)=>item.applicabilityId===current)
          ? current
          : next.applicability[0]?.applicabilityId || ""
      );

      const confirmedApplicable=next.applicability.flatMap((item)=>
        item.decisions
          .filter((decision)=>decision.status==="CONFIRMED" && decision.decisionType==="APPLICABLE")
          .map((decision)=>({...decision,schoolId:item.schoolId}))
      );
      setSelectedDecisionId((current)=>
        confirmedApplicable.some((item)=>item.decisionId===current)
          ? current
          : confirmedApplicable[0]?.decisionId || ""
      );

      setSelectedReviewId((current)=>
        next.changeReviews.some((item)=>item.reviewId===current)
          ? current
          : next.changeReviews[0]?.reviewId || ""
      );
    });
    return()=>{active=false;};
  },[client,refresh]);

  const selectedSource=useMemo(
    ()=>catalog?.sources.find((item)=>item.sourceId===selectedSourceId) || null,
    [catalog,selectedSourceId]
  );

  const verifiedRules=useMemo(
    ()=>catalog?.sources.flatMap((source)=>source.rules.filter((rule)=>rule.status==="VERIFIED")) || [],
    [catalog]
  );

  const selectedSchool=useMemo(
    ()=>catalog?.schools.find((item)=>item.schoolId===selectedSchoolId) || null,
    [catalog,selectedSchoolId]
  );

  useEffect(()=>{
    if(!selectedSchool){
      setSelectedYearId("");
      return;
    }
    setSelectedYearId((current)=>
      selectedSchool.academicYears.some((year)=>year.academicYearId===current)
        ? current
        : selectedSchool.academicYears.find((year)=>year.status==="ACTIVE")?.academicYearId
          || selectedSchool.academicYears[0]?.academicYearId
          || ""
    );
  },[selectedSchool]);

  const selectedApplicability=useMemo(
    ()=>catalog?.applicability.find((item)=>item.applicabilityId===selectedApplicabilityId) || null,
    [catalog,selectedApplicabilityId]
  );

  const confirmedApplicableDecisions=useMemo(
    ()=>catalog?.applicability.flatMap((item)=>
      item.decisions
        .filter((decision)=>decision.status==="CONFIRMED" && decision.decisionType==="APPLICABLE")
        .map((decision)=>({
          ...decision,
          schoolId:item.schoolId,
          schoolName:item.schoolName,
          ruleTitle:item.ruleTitle,
          yearCode:item.yearCode
        }))
    ) || [],
    [catalog]
  );

  const selectedConfirmedDecision=useMemo(
    ()=>confirmedApplicableDecisions.find((item)=>item.decisionId===selectedDecisionId) || null,
    [confirmedApplicableDecisions,selectedDecisionId]
  );

  useEffect(()=>{
    if(!selectedConfirmedDecision){
      setSchoolMembers([]);
      setAssigneeId("");
      return;
    }

    let active=true;
    client.rpc("admin_assurance_school_members",{
      p_school_id:selectedConfirmedDecision.schoolId
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const rows=(data || []) as SchoolMember[];
      setSchoolMembers(rows);
      setAssigneeId((current)=>
        rows.some((item)=>item.personId===current)
          ? current
          : rows[0]?.personId || ""
      );
    });
    return()=>{active=false;};
  },[client,selectedConfirmedDecision]);

  const selectedReview=useMemo(
    ()=>catalog?.changeReviews.find((item)=>item.reviewId===selectedReviewId) || null,
    [catalog,selectedReviewId]
  );

  async function run(
    key:string,
    action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>
  ){
    setBusy(key);setError("");
    try{
      const result=await action();
      if(result.error)throw new Error(result.error.message);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{
      setBusy("");
    }
  }

  async function createSource(){
    let snapshot:Record<string,unknown>;
    try{
      snapshot=parseObject(sourceSnapshot);
    }catch(err){
      setError(err instanceof Error ? err.message : "Source JSON 오류");
      return;
    }

    const data=await run("source-create",()=>client.rpc("admin_create_assurance_source",{
      p_source_key:sourceKey,
      p_source_type:sourceType,
      p_jurisdiction_code:jurisdiction,
      p_authority_name:authority,
      p_title:sourceTitle,
      p_canonical_uri:canonicalUri || null,
      p_source_snapshot:snapshot,
      p_effective_from:null,
      p_effective_until:null
    }));

    if(data && typeof data==="object" && "sourceId" in data){
      setSelectedSourceId(String((data as {sourceId:string}).sourceId));
      setSourceKey("");
      setSourceTitle("");
      setAuthority("");
      setCanonicalUri("");
    }
  }

  async function createRule(){
    if(!selectedSource)return;
    const currentVersion=selectedSource.versions.find((version)=>
      version.versionNo===selectedSource.currentVersion
    );
    if(!currentVersion){
      setError("현재 Source Version을 찾을 수 없습니다.");
      return;
    }

    const id=await run("rule-create",()=>client.rpc("admin_create_assurance_rule",{
      p_source_version_id:currentVersion.versionId,
      p_rule_key:ruleKey,
      p_title:ruleTitle,
      p_rule_text:ruleText,
      p_obligation_type:obligation,
      p_severity:ruleSeverity,
      p_applicability_spec:{mode:"HUMAN_GATE"},
      p_action_spec:{mode:"WORK_ITEM"},
      p_evidence_spec:{required:true}
    }));

    if(id){
      setRuleKey("");
      setRuleTitle("");
      setRuleText("");
    }
  }

  async function appendChange(){
    if(!selectedSource)return;
    let snapshot:Record<string,unknown>;
    try{
      snapshot=parseObject(changeSnapshot);
    }catch(err){
      setError(err instanceof Error ? err.message : "변경 JSON 오류");
      return;
    }

    await run("source-change",()=>client.rpc("admin_append_assurance_source_change",{
      p_source_id:selectedSource.sourceId,
      p_source_snapshot:snapshot,
      p_effective_from:null,
      p_effective_until:null,
      p_change_type:changeType,
      p_severity:changeSeverity,
      p_required_approvals:Number(changeApprovals),
      p_impact_scope:{mode:"MANUAL_REVIEW"}
    }));
  }

  async function createCandidate(){
    await run("candidate-create",()=>client.rpc("admin_create_assurance_applicability_candidate",{
      p_rule_id:selectedRuleId,
      p_school_id:selectedSchoolId,
      p_academic_year_id:selectedYearId,
      p_grade_scope:gradeScope.split(",").map((value)=>value.trim()).filter(Boolean),
      p_operator_note:candidateNote || null
    }));
  }

  async function createDecision(){
    if(!selectedApplicability)return;
    const id=await run("decision-create",()=>client.rpc("admin_create_assurance_school_decision",{
      p_applicability_id:selectedApplicability.applicabilityId,
      p_decision_type:decisionType,
      p_reason:decisionReason || null
    }));
    if(id)setDecisionReason("");
  }

  async function createWorkItem(){
    if(!selectedConfirmedDecision)return;
    await run("work-create",()=>client.rpc("admin_create_assurance_work_item",{
      p_decision_id:selectedConfirmedDecision.decisionId,
      p_assignee_person_id:assigneeId,
      p_title:workTitle,
      p_rationale:workRationale,
      p_due_at:dueAt ? new Date(dueAt).toISOString() : null,
      p_evidence_requirement:{type:"REVIEW_NOTE",required:true}
    }));
    setWorkTitle("");
    setWorkRationale("");
    setDueAt("");
  }

  async function decideReview(){
    if(!selectedReview)return;
    await run("review-decision",()=>client.rpc("admin_decide_assurance_change_review",{
      p_review_id:selectedReview.reviewId,
      p_expected_version:selectedReview.version,
      p_decision:reviewDecision,
      p_checklist:checks,
      p_task_action:taskAction,
      p_audit_reason:auditReason,
      p_notes:reviewNotes || null
    }));
    setAuditReason("");
    setReviewNotes("");
  }

  async function advanceReview(toStatus:string){
    if(!selectedReview)return;
    await run("review-advance",()=>client.rpc("admin_advance_assurance_change_review",{
      p_review_id:selectedReview.reviewId,
      p_expected_version:selectedReview.version,
      p_to_status:toStatus,
      p_reason:"Admin Governance Workbench 전이"
    }));
  }

  if(!catalog)return <p className="muted">Governance 원장을 불러오는 중…</p>;

  return (
    <section className="admin-assurance-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Governance / Assurance</p>
          <h2>정책·검토</h2>
          <p className="route-description">
            정책 근거를 버전 관리하고, 학교 적용 여부와 실제 조치 사이에 사람의 최종 검토를 둡니다.
          </p>
        </div>
        <span className="phase-badge">BUILD-12</span>
      </div>

      {error && <p className="error">{error}</p>}

      <AdminAssurancePolicyPackPanel client={client} />

      <div className="governance-tabs">
        {([
          ["SOURCE","근거·규칙"],
          ["APPLICABILITY","학교 적용판정"],
          ["WORK","Work Item"],
          ["CHANGE","Source 변경검토"]
        ] as Array<[Tab,string]>).map(([value,label])=>(
          <button
            key={value}
            className={tab===value ? "filter-chip active" : "filter-chip"}
            onClick={()=>setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab==="SOURCE" && (
        <div className="governance-source-layout">
          <aside className="governance-list-panel">
            <div className="panel-title">
              <strong>Source</strong>
              <span>{catalog.sources.length}건</span>
            </div>
            {catalog.sources.map((source)=>(
              <button
                key={source.sourceId}
                className={selectedSourceId===source.sourceId ? "governance-list-row active" : "governance-list-row"}
                onClick={()=>setSelectedSourceId(source.sourceId)}
              >
                <strong>{source.title}</strong>
                <span>{source.authorityName}</span>
                <small>v{source.currentVersion} · {sourceStatus(source.status)}</small>
              </button>
            ))}
            {!catalog.sources.length && <p className="case-empty">등록된 Assurance Source가 없습니다.</p>}
          </aside>

          <div className="governance-detail-panel">
            <section className="governance-card">
              <div className="panel-title">
                <strong>새 Source</strong>
                <span>원문·출처 버전관리</span>
              </div>
              <div className="governance-form-grid">
                <label>Source Key<input value={sourceKey} onChange={(e)=>setSourceKey(e.target.value)} /></label>
                <label>
                  유형
                  <select value={sourceType} onChange={(e)=>setSourceType(e.target.value)}>
                    <option value="LAW">법령</option>
                    <option value="REGULATION">규정</option>
                    <option value="POLICY">정책</option>
                    <option value="GUIDELINE">가이드라인</option>
                    <option value="CURRICULUM">교육과정</option>
                    <option value="OFFICIAL_NOTICE">공문</option>
                    <option value="SCHOOL_PLAN">학교계획</option>
                    <option value="OTHER">기타</option>
                  </select>
                </label>
                <label>관할 코드<input value={jurisdiction} onChange={(e)=>setJurisdiction(e.target.value)} /></label>
                <label>발행기관<input value={authority} onChange={(e)=>setAuthority(e.target.value)} /></label>
                <label className="wide">제목<input value={sourceTitle} onChange={(e)=>setSourceTitle(e.target.value)} /></label>
                <label className="wide">Canonical URI<input value={canonicalUri} onChange={(e)=>setCanonicalUri(e.target.value)} /></label>
                <label className="wide">
                  Source Snapshot JSON
                  <textarea value={sourceSnapshot} onChange={(e)=>setSourceSnapshot(e.target.value)} />
                </label>
              </div>
              <button
                className="primary compact-button"
                disabled={busy!=="" || !sourceKey || !authority || !sourceTitle}
                onClick={createSource}
              >
                Source v1 생성
              </button>
            </section>

            {selectedSource && (
              <>
                <section className="governance-card">
                  <div className="governance-source-head">
                    <div>
                      <p className="eyebrow">{selectedSource.sourceKey}</p>
                      <h3>{selectedSource.title}</h3>
                      <p>{selectedSource.authorityName} · {selectedSource.jurisdictionCode}</p>
                    </div>
                    <div className="program-status-actions">
                      <span className={selectedSource.status==="VERIFIED" ? "status-pill good" : "status-pill"}>
                        {sourceStatus(selectedSource.status)}
                      </span>
                      {selectedSource.status==="DRAFT" && selectedSource.currentVersion>0 && (
                        <button
                          className="primary compact-button"
                          disabled={busy!==""}
                          onClick={()=>run("source-verify",()=>client.rpc("admin_verify_assurance_source",{
                            p_source_id:selectedSource.sourceId,
                            p_expected_current_version:selectedSource.currentVersion
                          }))}
                        >
                          Source 검증
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="source-version-list">
                    {selectedSource.versions.map((version)=>(
                      <div className="source-version-row" key={version.versionId}>
                        <div>
                          <strong>v{version.versionNo}</strong>
                          <span>{version.changeType}</span>
                          <small>{version.contentHash}</small>
                        </div>
                        <span>{formatDate(version.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {selectedSource.status==="VERIFIED" && (
                  <section className="governance-card">
                    <div className="panel-title">
                      <strong>Source 변경 버전</strong>
                      <span>v2+는 Change Review 자동개설</span>
                    </div>
                    <div className="governance-form-grid">
                      <label>
                        변경 유형
                        <select value={changeType} onChange={(e)=>setChangeType(e.target.value)}>
                          <option value="METADATA_ONLY">메타데이터</option>
                          <option value="SEMANTIC">의미 변경</option>
                          <option value="REPEAL">폐지</option>
                        </select>
                      </label>
                      <label>
                        심각도
                        <select value={changeSeverity} onChange={(e)=>setChangeSeverity(e.target.value)}>
                          <option value="P0">P0</option>
                          <option value="P1">P1</option>
                          <option value="P2">P2</option>
                          <option value="P3">P3</option>
                        </select>
                      </label>
                      <label>
                        필요 승인수
                        <select value={changeApprovals} onChange={(e)=>setChangeApprovals(e.target.value)}>
                          <option value="1">1명</option>
                          <option value="2">2명</option>
                        </select>
                      </label>
                      <label className="wide">
                        변경 Snapshot JSON
                        <textarea value={changeSnapshot} onChange={(e)=>setChangeSnapshot(e.target.value)} />
                      </label>
                    </div>
                    <button className="secondary" disabled={busy!==""} onClick={appendChange}>
                      새 Source Version + Change Review
                    </button>
                  </section>
                )}

                <section className="governance-card">
                  <div className="panel-title">
                    <strong>Rule</strong>
                    <span>{selectedSource.rules.length}개</span>
                  </div>

                  {selectedSource.status==="VERIFIED" && (
                    <div className="governance-form-grid rule-create-form">
                      <label>Rule Key<input value={ruleKey} onChange={(e)=>setRuleKey(e.target.value)} /></label>
                      <label>
                        의무유형
                        <select value={obligation} onChange={(e)=>setObligation(e.target.value)}>
                          <option value="REQUIRED">필수</option>
                          <option value="RECOMMENDED">권고</option>
                          <option value="OPTIONAL">선택</option>
                          <option value="PROHIBITED">금지</option>
                          <option value="INFORMATION">정보</option>
                        </select>
                      </label>
                      <label>
                        심각도
                        <select value={ruleSeverity} onChange={(e)=>setRuleSeverity(e.target.value)}>
                          <option value="P0">P0</option>
                          <option value="P1">P1</option>
                          <option value="P2">P2</option>
                          <option value="P3">P3</option>
                        </select>
                      </label>
                      <label className="wide">제목<input value={ruleTitle} onChange={(e)=>setRuleTitle(e.target.value)} /></label>
                      <label className="wide">Rule Text<textarea value={ruleText} onChange={(e)=>setRuleText(e.target.value)} /></label>
                      <button
                        className="primary compact-button"
                        disabled={busy!=="" || !ruleKey || !ruleTitle || !ruleText}
                        onClick={createRule}
                      >
                        Rule 생성
                      </button>
                    </div>
                  )}

                  <div className="governance-rule-list">
                    {selectedSource.rules.map((rule)=>(
                      <article className="governance-rule-card" key={rule.ruleId}>
                        <div>
                          <span className={"severity-chip "+rule.severity.toLowerCase()}>{rule.severity}</span>
                          <span className="journey-type-chip">{rule.obligationType}</span>
                        </div>
                        <strong>{rule.title}</strong>
                        <p>{rule.ruleText}</p>
                        <small>{rule.ruleKey} · v{rule.versionNo}</small>
                        <div className="program-status-actions">
                          <span className={rule.status==="VERIFIED" ? "status-pill good" : "status-pill"}>
                            {rule.status==="VERIFIED" ? "검증 완료" : "초안"}
                          </span>
                          {rule.status==="DRAFT" && (
                            <button
                              className="secondary"
                              disabled={busy!==""}
                              onClick={()=>run("rule-verify",()=>client.rpc("admin_verify_assurance_rule",{
                                p_rule_id:rule.ruleId
                              }))}
                            >
                              Rule 검증
                            </button>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      )}

      {tab==="APPLICABILITY" && (
        <div className="governance-two-column">
          <section className="governance-card">
            <div className="panel-title">
              <strong>학교 적용 후보</strong>
              <span>자동확정 금지</span>
            </div>
            <div className="governance-form-grid">
              <label>
                검증 Rule
                <select value={selectedRuleId} onChange={(e)=>setSelectedRuleId(e.target.value)}>
                  {verifiedRules.map((rule)=>(
                    <option key={rule.ruleId} value={rule.ruleId}>
                      {rule.severity} · {rule.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                학교
                <select value={selectedSchoolId} onChange={(e)=>setSelectedSchoolId(e.target.value)}>
                  {catalog.schools.map((school)=>(
                    <option key={school.schoolId} value={school.schoolId}>{school.schoolName}</option>
                  ))}
                </select>
              </label>
              <label>
                학년도
                <select value={selectedYearId} onChange={(e)=>setSelectedYearId(e.target.value)}>
                  {(selectedSchool?.academicYears || []).map((year)=>(
                    <option key={year.academicYearId} value={year.academicYearId}>
                      {year.yearCode} · {year.status}
                    </option>
                  ))}
                </select>
              </label>
              <label>학년 범위<input value={gradeScope} onChange={(e)=>setGradeScope(e.target.value)} placeholder="7,8,9" /></label>
              <label className="wide">후보 메모<input value={candidateNote} onChange={(e)=>setCandidateNote(e.target.value)} /></label>
            </div>
            <button
              className="primary compact-button"
              disabled={busy!=="" || !selectedRuleId || !selectedSchoolId || !selectedYearId}
              onClick={createCandidate}
            >
              CANDIDATE 생성
            </button>
          </section>

          <section className="governance-card">
            <div className="panel-title">
              <strong>Human Decision</strong>
              <span>{catalog.applicability.length} 후보</span>
            </div>
            <label className="governance-select">
              적용 후보
              <select value={selectedApplicabilityId} onChange={(e)=>setSelectedApplicabilityId(e.target.value)}>
                {catalog.applicability.map((item)=>(
                  <option key={item.applicabilityId} value={item.applicabilityId}>
                    {item.schoolName} · {item.yearCode} · {item.ruleTitle}
                  </option>
                ))}
              </select>
            </label>

            {selectedApplicability && (
              <>
                <div className="applicability-summary">
                  <span className={"severity-chip "+selectedApplicability.ruleSeverity.toLowerCase()}>
                    {selectedApplicability.ruleSeverity}
                  </span>
                  <strong>{selectedApplicability.ruleTitle}</strong>
                  <p>{selectedApplicability.schoolName} · {selectedApplicability.yearCode}</p>
                  <small>Engine: {selectedApplicability.engineOutcome} · Human Gate 필수</small>
                </div>

                <div className="decision-create-row">
                  <select value={decisionType} onChange={(e)=>setDecisionType(e.target.value)}>
                    <option value="APPLICABLE">적용</option>
                    <option value="NOT_APPLICABLE">미적용</option>
                    <option value="DEFERRED">보류</option>
                    <option value="REQUEST_CLARIFICATION">추가확인</option>
                  </select>
                  <input value={decisionReason} onChange={(e)=>setDecisionReason(e.target.value)} placeholder="판단 근거" />
                  <button className="secondary" disabled={busy!=="" || !decisionReason.trim()} onClick={createDecision}>
                    DRAFT Decision
                  </button>
                </div>

                <div className="decision-list">
                  {selectedApplicability.decisions.map((decision)=>(
                    <div className="review-line" key={decision.decisionId}>
                      <div>
                        <strong>{decision.decisionType}</strong>
                        <span>{decision.reason || "근거 없음"}</span>
                      </div>
                      <div className="program-status-actions">
                        <span className={decision.status==="CONFIRMED" ? "status-pill good" : "status-pill"}>
                          {decision.status}
                        </span>
                        {decision.status==="DRAFT" && (
                          <button
                            className="primary compact-button"
                            disabled={busy!==""}
                            onClick={()=>run("decision-confirm",()=>client.rpc("admin_confirm_assurance_school_decision",{
                              p_decision_id:decision.decisionId,
                              p_expected_version:decision.version,
                              p_reason:decision.reason || "Admin Human Decision 확정"
                            }))}
                          >
                            결정 확정
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {tab==="WORK" && (
        <div className="governance-two-column">
          <section className="governance-card">
            <div className="panel-title">
              <strong>Work Item 생성</strong>
              <span>확정된 APPLICABLE Decision만</span>
            </div>
            <div className="governance-form-grid">
              <label className="wide">
                Decision
                <select value={selectedDecisionId} onChange={(e)=>setSelectedDecisionId(e.target.value)}>
                  {confirmedApplicableDecisions.map((item)=>(
                    <option key={item.decisionId} value={item.decisionId}>
                      {item.schoolName} · {item.yearCode} · {item.ruleTitle}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                담당자
                <select value={assigneeId} onChange={(e)=>setAssigneeId(e.target.value)}>
                  {schoolMembers.map((member)=>(
                    <option key={member.personId} value={member.personId}>
                      {member.displayName} · {member.roles.join(", ")}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                기한
                <input type="datetime-local" value={dueAt} onChange={(e)=>setDueAt(e.target.value)} />
              </label>
              <label className="wide">업무명<input value={workTitle} onChange={(e)=>setWorkTitle(e.target.value)} /></label>
              <label className="wide">필요 이유<textarea value={workRationale} onChange={(e)=>setWorkRationale(e.target.value)} /></label>
            </div>
            <button
              className="primary compact-button"
              disabled={busy!=="" || !selectedDecisionId || !assigneeId || !workTitle || !workRationale || !dueAt}
              onClick={createWorkItem}
            >
              Work Item 생성
            </button>
          </section>

          <section className="governance-card">
            <div className="panel-title">
              <strong>운영 Work Item</strong>
              <span>{catalog.workItems.length}건</span>
            </div>

            <label className="governance-select">
              완료 메모
              <input value={completionNote} onChange={(e)=>setCompletionNote(e.target.value)} placeholder="COMPLETED 전환 시 필수" />
            </label>

            <div className="governance-work-list">
              {catalog.workItems.map((item)=>(
                <article className={"governance-work-card status-"+item.status.toLowerCase()} key={item.workItemId}>
                  <div className="governance-work-head">
                    <div>
                      <span className={"severity-chip "+item.ruleSeverity.toLowerCase()}>{item.ruleSeverity}</span>
                      <strong>{item.title}</strong>
                    </div>
                    <span className={item.status==="COMPLETED" ? "status-pill good" : "status-pill"}>
                      {workStatus(item.status)}
                    </span>
                  </div>
                  <p>{item.schoolName} · {item.assigneeName}</p>
                  <small>기한 {formatDate(item.dueAt)} · {item.ruleTitle}</small>
                  <div className="program-status-actions">
                    {item.status==="PROPOSED" && (
                      <>
                        <button className="secondary" disabled={busy!==""} onClick={()=>run("work-approved",()=>client.rpc("admin_transition_assurance_work_item",{
                          p_work_item_id:item.workItemId,p_expected_version:item.version,p_to_status:"APPROVED",
                          p_reason:"Admin 승인",p_completion_note:null
                        }))}>승인</button>
                        <button className="secondary danger" disabled={busy!==""} onClick={()=>run("work-blocked",()=>client.rpc("admin_transition_assurance_work_item",{
                          p_work_item_id:item.workItemId,p_expected_version:item.version,p_to_status:"BLOCKED",
                          p_reason:"Admin 차단",p_completion_note:null
                        }))}>차단</button>
                      </>
                    )}
                    {item.status==="APPROVED" && (
                      <button className="primary compact-button" disabled={busy!==""} onClick={()=>run("work-start",()=>client.rpc("admin_transition_assurance_work_item",{
                        p_work_item_id:item.workItemId,p_expected_version:item.version,p_to_status:"IN_PROGRESS",
                        p_reason:"Admin 진행 시작",p_completion_note:null
                      }))}>진행 시작</button>
                    )}
                    {item.status==="IN_PROGRESS" && (
                      <button className="primary compact-button" disabled={busy!=="" || !completionNote.trim()} onClick={()=>run("work-complete",()=>client.rpc("admin_transition_assurance_work_item",{
                        p_work_item_id:item.workItemId,p_expected_version:item.version,p_to_status:"COMPLETED",
                        p_reason:"Admin 완료 확인",p_completion_note:completionNote.trim()
                      }))}>완료</button>
                    )}
                  </div>
                </article>
              ))}
              {!catalog.workItems.length && <p className="case-empty">Assurance Work Item이 없습니다.</p>}
            </div>
          </section>
        </div>
      )}

      {tab==="CHANGE" && (
        <div className="governance-change-layout">
          <aside className="governance-list-panel">
            <div className="panel-title">
              <strong>Change Review</strong>
              <span>{catalog.changeReviews.length}건</span>
            </div>
            {catalog.changeReviews.map((review)=>(
              <button
                key={review.reviewId}
                className={selectedReviewId===review.reviewId ? "governance-list-row active" : "governance-list-row"}
                onClick={()=>setSelectedReviewId(review.reviewId)}
              >
                <strong>{review.sourceTitle}</strong>
                <span>{review.changeType}</span>
                <small>{review.severity} · {reviewStatus(review.status)}</small>
              </button>
            ))}
            {!catalog.changeReviews.length && <p className="case-empty">변경 검토가 없습니다.</p>}
          </aside>

          <div className="governance-detail-panel">
            {!selectedReview ? <p className="case-empty">Change Review를 선택하세요.</p> : (
              <>
                <header className="governance-review-head">
                  <div>
                    <p className="eyebrow">{selectedReview.changeType}</p>
                    <h2>{selectedReview.sourceTitle}</h2>
                    <p>필요 승인 {selectedReview.requiredApprovals}명 · Guidance Freeze {selectedReview.guidanceFrozen ? "ON" : "OFF"}</p>
                  </div>
                  <div className="program-status-actions">
                    <span className={"severity-chip "+selectedReview.severity.toLowerCase()}>{selectedReview.severity}</span>
                    <span className="status-pill">{reviewStatus(selectedReview.status)}</span>
                  </div>
                </header>

                {["HUMAN_REVIEW_REQUIRED","EVIDENCE_PENDING","ESCALATED"].includes(selectedReview.status) && (
                  <section className="governance-card">
                    <div className="panel-title">
                      <strong>4-check Human Review</strong>
                      <span>Source / Meaning / Scope / Impact</span>
                    </div>
                    <div className="four-check-grid">
                      {(["source","meaning","scope","impact"] as const).map((key)=>(
                        <label key={key}>
                          <input
                            type="checkbox"
                            checked={checks[key]}
                            onChange={(e)=>setChecks((current)=>({...current,[key]:e.target.checked}))}
                          />
                          {key}
                        </label>
                      ))}
                    </div>
                    <div className="governance-form-grid">
                      <label>
                        Decision
                        <select value={reviewDecision} onChange={(e)=>setReviewDecision(e.target.value)}>
                          <option value="APPROVE_CHANGE">변경 승인</option>
                          <option value="DISMISS_NO_SEMANTIC_CHANGE">의미변화 없음</option>
                          <option value="REQUEST_MORE_EVIDENCE">추가근거 요청</option>
                          <option value="ESCALATE">상위검토</option>
                        </select>
                      </label>
                      <label>
                        Task Action
                        <select value={taskAction} onChange={(e)=>setTaskAction(e.target.value)}>
                          <option value="NO_CHANGE">변경 없음</option>
                          <option value="MARK_REVIEW_REQUIRED">검토 필요 표시</option>
                          <option value="UPDATE_AFTER_APPROVAL">승인 후 업데이트</option>
                          <option value="CANCEL_FUTURE_ONLY">향후 업무만 취소</option>
                        </select>
                      </label>
                      <label className="wide">Audit Reason<input value={auditReason} onChange={(e)=>setAuditReason(e.target.value)} /></label>
                      <label className="wide">메모<textarea value={reviewNotes} onChange={(e)=>setReviewNotes(e.target.value)} /></label>
                    </div>
                    <button
                      className="primary compact-button"
                      disabled={busy!=="" || !auditReason.trim()}
                      onClick={decideReview}
                    >
                      Human Decision 기록
                    </button>
                  </section>
                )}

                <section className="governance-card">
                  <div className="panel-title">
                    <strong>후속 Gate</strong>
                    <span>{reviewStatus(selectedReview.status)}</span>
                  </div>
                  <div className="program-status-actions governance-advance-actions">
                    {selectedReview.status==="IMPACT_CONFIRMED" && (
                      <button className="primary compact-button" onClick={()=>advanceReview("SCHOOL_APPLICABILITY_RECHECK")}>
                        학교 적용 재검토
                      </button>
                    )}
                    {selectedReview.status==="SCHOOL_APPLICABILITY_RECHECK" && (
                      <>
                        <button className="secondary" onClick={()=>advanceReview("VERIFIED_NO_TEACHER_ACTION")}>
                          교사 조치 없음
                        </button>
                        <button className="primary compact-button" onClick={()=>advanceReview("TEACHER_QUEUE_READY")}>
                          교사 업무 반영
                        </button>
                      </>
                    )}
                    {selectedReview.status==="TEACHER_QUEUE_READY" && (
                      <button className="primary compact-button" onClick={()=>advanceReview("TASK_UPDATE_APPROVED")}>
                        업무 변경 승인
                      </button>
                    )}
                    {selectedReview.status==="TASK_UPDATE_APPROVED" && (
                      <button className="primary compact-button" onClick={()=>advanceReview("CLOSED")}>
                        Change Review 종료
                      </button>
                    )}
                  </div>
                </section>

                <section className="governance-card">
                  <div className="panel-title">
                    <strong>Decision Log</strong>
                    <span>{selectedReview.decisions.length}건</span>
                  </div>
                  {selectedReview.decisions.map((decision)=>(
                    <div className="review-line" key={decision.decisionId}>
                      <div>
                        <strong>{decision.decision}</strong>
                        <span>{decision.reviewerName} · {decision.taskAction}</span>
                        <small>{decision.auditReason}</small>
                      </div>
                      <span>{formatDate(decision.decidedAt)}</span>
                    </div>
                  ))}
                  {!selectedReview.decisions.length && <p className="case-empty">아직 Human Decision이 없습니다.</p>}
                </section>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
